import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    computeMachineFacets,
    countActiveMachineFilters,
    groupModelOptions,
    orderMakeOptions,
    pruneModelsForMakes,
    yearHistogram,
    yearQuickPicks,
    yearSelectOptions,
} from '../../utils/machineFilters';

const unitsLabel = (n) => `${n.toLocaleString('en-US')} ${n === 1 ? 'unit' : 'units'}`;

function OptionRow({ label, display, count, checked, onToggle }) {
    return (
        <label className={`machine-opt ${count === 0 ? 'is-zero' : ''}`}>
            <input
                type="checkbox"
                checked={checked}
                onChange={onToggle}
                aria-label={`${label}, ${unitsLabel(count)}`}
            />
            <span className="machine-opt-name">{display || label}</span>
            <span className="machine-opt-count" aria-hidden="true">{count.toLocaleString('en-US')}</span>
        </label>
    );
}

OptionRow.propTypes = {
    label: PropTypes.string.isRequired,
    display: PropTypes.node,
    count: PropTypes.number.isRequired,
    checked: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
};

OptionRow.defaultProps = { display: null };

/**
 * "Machine" button in the filter row and its Make / Model / Year panel.
 * Options come from the loaded units (`catalog`); counts come from
 * `facetData`, the units that pass every other filter on the page.
 */
function MachineFilter({
    filters, onChange, catalog, facetData, currentYear, resultCount, buttonRef: externalButtonRef,
    // React 19 ignores defaultProps on function components: defaults here.
    buttonClassName = 'btn-more-filters btn-machine', badgeClassName = 'filter-count-badge',
}) {
    const [open, setOpen] = useState(false);
    const [makeQuery, setMakeQuery] = useState('');
    const [modelQuery, setModelQuery] = useState('');
    const [panelOffset, setPanelOffset] = useState(0);
    const wrapRef = useRef(null);
    const ownButtonRef = useRef(null);
    const buttonRef = externalButtonRef || ownButtonRef;
    const ids = useId();

    const makes = filters.makes || [];
    const models = filters.models || [];
    const activeCount = countActiveMachineFilters(filters);

    // Closing always forgets the searches, so the lists are whole on reopen.
    const closePanel = () => {
        setOpen(false);
        setMakeQuery('');
        setModelQuery('');
    };

    // A model search typed for one make would hide the next make's models.
    const makesKey = (filters.makes || []).join('|');
    useEffect(() => { setModelQuery(''); }, [makesKey]);

    useEffect(() => {
        if (!open) return undefined;
        const closeAndRefocus = () => {
            closePanel();
            buttonRef.current?.focus();
        };
        const onPointerDown = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) closePanel();
        };
        const onKeyDown = (event) => {
            if (event.key !== 'Escape') return;
            event.stopPropagation();
            closeAndRefocus();
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Counts are only needed while the panel is open.
    const facets = useMemo(
        () => (open ? computeMachineFacets(facetData, filters) : null),
        [open, facetData, filters],
    );
    const makeOptions = useMemo(
        () => (facets ? orderMakeOptions(catalog, facets.makeCounts, makeQuery) : { top: [], rest: [] }),
        [catalog, facets, makeQuery],
    );
    const modelGroups = useMemo(
        () => (facets ? groupModelOptions(catalog, makes, facets.modelCounts, modelQuery) : []),
        [catalog, facets, filters.makes, modelQuery], // eslint-disable-line react-hooks/exhaustive-deps
    );
    const bars = useMemo(
        () => (facets ? yearHistogram(facets.yearCounts, catalog.years, filters) : []),
        [facets, catalog.years, filters],
    );

    // The panel is wider than the button; shift it left as far as needed to
    // stay inside the viewport (16px gutter), wherever the row wrapped it.
    const toggleOpen = () => {
        if (open) {
            closePanel();
            return;
        }
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const gutter = 16;
            // The visible width: clientWidth leaves out a classic scrollbar,
            // so the gutter is a real 16px from what the user sees.
            const viewport = document.documentElement.clientWidth || window.innerWidth;
            const panelWidth = Math.min(820, viewport - 2 * gutter);
            const left = Math.max(gutter, Math.min(rect.left, viewport - gutter - panelWidth));
            setPanelOffset(Math.round(left - rect.left));
        }
        setOpen((v) => !v);
    };

    const update = (patch) => onChange({ ...filters, ...patch });

    const toggleMake = (key) => {
        const next = makes.includes(key) ? makes.filter((k) => k !== key) : [...makes, key];
        update({ makes: next, models: pruneModelsForMakes(models, next) });
    };
    const toggleModel = (id) => {
        update({ models: models.includes(id) ? models.filter((m) => m !== id) : [...models, id] });
    };
    const setFrom = (value) => {
        const from = value === '' ? null : Number(value);
        const to = filters.yearTo != null && from != null && from > filters.yearTo ? from : filters.yearTo ?? null;
        update({ yearFrom: from, yearTo: to });
    };
    const setTo = (value) => {
        const to = value === '' ? null : Number(value);
        const from = filters.yearFrom != null && to != null && filters.yearFrom > to ? to : filters.yearFrom ?? null;
        update({ yearFrom: from, yearTo: to });
    };
    const applyQuickPick = (pick, pressed) => {
        update(pressed ? { yearFrom: null, yearTo: null } : { yearFrom: pick.from, yearTo: pick.to });
    };
    const clearMachine = () => update({ makes: [], models: [], yearFrom: null, yearTo: null, includeNoYear: true });

    const renderMakeRow = (m) => (
        <OptionRow
            key={m.key}
            label={m.label}
            count={m.count}
            checked={makes.includes(m.key)}
            onToggle={() => toggleMake(m.key)}
        />
    );

    const selectYears = yearSelectOptions(catalog.years, filters);
    const maxBar = Math.max(1, ...bars.map((b) => b.count));
    const onlyMakeLabel = makes.length === 1 ? catalog.makes.get(makes[0])?.label : null;

    return (
        <div className="machine-filter" ref={wrapRef}>
            <button
                ref={buttonRef}
                type="button"
                className={`${buttonClassName} ${activeCount > 0 ? 'has-filters' : ''}`}
                aria-haspopup="dialog"
                aria-expanded={open}
                onClick={toggleOpen}
            >
                <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" aria-hidden="true">
                    <path d="M5 4a1 1 0 00-1 1v6H3a1 1 0 000 2h.27A2.5 2.5 0 108 14h4a2.5 2.5 0 104.73-1H17a1 1 0 001-1V9.5a1 1 0 00-.3-.7l-2.5-2.5A1 1 0 0014.5 6H12V5a1 1 0 00-1-1H5z" />
                </svg>
                Machine
                {activeCount > 0 && (
                    <span className={badgeClassName} data-testid="machine-badge">{activeCount}</span>
                )}
                <span className="machine-caret" aria-hidden="true">▼</span>
            </button>

            {open && facets && (
                <div className="machine-pop" style={{ left: panelOffset }} role="dialog" aria-label="Machine filters">
                    <section className="machine-col" role="group" aria-label="Make">
                        <h3 className="machine-col-title">Make</h3>
                        <input
                            type="search"
                            className="machine-search"
                            placeholder={`Search ${catalog.makes.size.toLocaleString('en-US')} makes`}
                            aria-label="Search makes"
                            value={makeQuery}
                            onChange={(e) => setMakeQuery(e.target.value)}
                        />
                        <div className="machine-list">
                            {makeOptions.top.length > 0 && (
                                <>
                                    <div className="machine-grp">Most common</div>
                                    {makeOptions.top.map(renderMakeRow)}
                                </>
                            )}
                            {makeOptions.rest.length > 0 && (
                                <>
                                    <div className="machine-grp">All makes A–Z</div>
                                    {makeOptions.rest.map(renderMakeRow)}
                                </>
                            )}
                            {makeOptions.top.length + makeOptions.rest.length === 0 && (
                                <p className="machine-empty">
                                    {catalog.makes.size === 0 ? 'No makes in this inventory' : `No make matches "${makeQuery}"`}
                                </p>
                            )}
                        </div>
                    </section>

                    <section className="machine-col" role="group" aria-label="Model">
                        <h3 className="machine-col-title">
                            Model
                            {onlyMakeLabel && <span className="machine-col-sub"> in {onlyMakeLabel}</span>}
                            {makes.length > 1 && <span className="machine-col-sub"> in {makes.length.toLocaleString('en-US')} makes</span>}
                        </h3>
                        <input
                            type="search"
                            className="machine-search"
                            placeholder="Search models"
                            aria-label="Search models"
                            value={modelQuery}
                            onChange={(e) => setModelQuery(e.target.value)}
                        />
                        <div className="machine-list">
                            {modelGroups.map((group) => (
                                <React.Fragment key={group.category}>
                                    <div className="machine-grp">{group.category}</div>
                                    {group.options.map((o) => (
                                        <OptionRow
                                            key={o.id}
                                            label={o.label}
                                            display={o.label === o.modelLabel ? null : (
                                                <>
                                                    <span className="machine-opt-make">{o.makeLabel} · </span>
                                                    {o.modelLabel}
                                                </>
                                            )}
                                            count={o.count}
                                            checked={models.includes(o.id)}
                                            onToggle={() => toggleModel(o.id)}
                                        />
                                    ))}
                                </React.Fragment>
                            ))}
                            {modelGroups.length === 0 && (
                                <p className="machine-empty">
                                    {catalog.models.size === 0 ? 'No models in this inventory' : 'No model matches'}
                                </p>
                            )}
                        </div>
                    </section>

                    <section className="machine-col" role="group" aria-label="Year">
                        <h3 className="machine-col-title">Year</h3>
                        <div className="machine-hist" data-testid="year-histogram" aria-hidden="true">
                            {bars.map((b) => (
                                <span
                                    key={b.label}
                                    className={b.inRange ? 'in-range' : ''}
                                    style={{ height: `${Math.max(6, (b.count / maxBar) * 100)}%` }}
                                    title={`${b.label}: ${b.count.toLocaleString('en-US')}`}
                                />
                            ))}
                        </div>
                        <div className="machine-year-range">
                            <label htmlFor={`${ids}-from`} className="machine-year-field">
                                <span>From</span>
                                <select
                                    id={`${ids}-from`}
                                    value={filters.yearFrom ?? ''}
                                    onChange={(e) => setFrom(e.target.value)}
                                >
                                    <option value="">Any</option>
                                    {selectYears.map((y) => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </label>
                            <span className="machine-year-dash" aria-hidden="true">–</span>
                            <label htmlFor={`${ids}-to`} className="machine-year-field">
                                <span>To</span>
                                <select
                                    id={`${ids}-to`}
                                    value={filters.yearTo ?? ''}
                                    onChange={(e) => setTo(e.target.value)}
                                >
                                    <option value="">Any</option>
                                    {selectYears.map((y) => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </label>
                        </div>
                        <div className="machine-quick">
                            {yearQuickPicks(currentYear).map((pick) => {
                                const pressed = (filters.yearFrom ?? null) === pick.from && (filters.yearTo ?? null) === pick.to;
                                return (
                                    <button
                                        key={pick.id}
                                        type="button"
                                        className="machine-quick-btn"
                                        aria-pressed={pressed}
                                        onClick={() => applyQuickPick(pick, pressed)}
                                    >
                                        {pick.label}
                                    </button>
                                );
                            })}
                        </div>
                        <label className="machine-noyear">
                            <input
                                type="checkbox"
                                checked={filters.includeNoYear !== false}
                                onChange={(e) => update({ includeNoYear: e.target.checked })}
                            />
                            Include units without a year
                            <span className="machine-opt-count">({facets.noYearCount.toLocaleString('en-US')})</span>
                        </label>
                    </section>

                    <div className="machine-foot">
                        {activeCount > 0 && (
                            <button type="button" className="aging-multiselect-action" onClick={clearMachine}>
                                Clear make, model and year
                            </button>
                        )}
                        <span className="machine-foot-note">
                            {resultCount != null && (
                                <strong className="machine-foot-count">
                                    {`Showing ${resultCount.toLocaleString('en-US')} ${resultCount === 1 ? 'unit' : 'units'}`}
                                </strong>
                            )}
                            Counts follow your other filters
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}

MachineFilter.propTypes = {
    filters: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    onChange: PropTypes.func.isRequired,
    catalog: PropTypes.shape({
        makes: PropTypes.instanceOf(Map).isRequired,
        models: PropTypes.instanceOf(Map).isRequired,
        years: PropTypes.arrayOf(PropTypes.number).isRequired,
    }).isRequired,
    facetData: PropTypes.arrayOf(PropTypes.object).isRequired,
    currentYear: PropTypes.number.isRequired,
    resultCount: PropTypes.number,
    buttonRef: PropTypes.shape({ current: PropTypes.any }),
    // The redesigned page styles the button with its own classes.
    buttonClassName: PropTypes.string,
    badgeClassName: PropTypes.string,
};

MachineFilter.defaultProps = {
    resultCount: null,
    buttonRef: null,
};

export default MachineFilter;
